import { useState } from "react";
import { Loader2, Play, Pause, Check } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const SLOTS = [
  { slot: "giulia", genere: "female", label: "Giulia (voce femminile)" },
  { slot: "marco", genere: "male", label: "Marco (voce maschile)" },
];

let player = null;

const VoiceRow = ({ v, slot, chosen, busy, onUse, playing, onPlay }) => (
  <li className="flex items-center gap-3 rounded-2xl border border-[#e7e7ea] bg-white p-3" data-testid={`voice-row-${v.voice_id}`}>
    <button type="button" onClick={() => onPlay(v)} className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#111111] text-white transition-transform hover:scale-105" aria-label="Ascolta" data-testid={`voice-preview-${v.voice_id}`}>
      {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
    </button>
    <div className="min-w-0 flex-1">
      <p className="truncate font-semibold">{v.name}</p>
      <p className="truncate text-xs text-[#52525b]">{[v.accent, v.age, v.descriptive, v.use_case].filter(Boolean).join(" · ")}</p>
    </div>
    {chosen ? (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700"><Check className="h-3.5 w-3.5" /> In uso</span>
    ) : (
      <button type="button" onClick={() => onUse(v, slot)} disabled={busy} className="btn-ghost h-9 shrink-0 px-3.5 text-xs" data-testid={`voice-use-${v.voice_id}`}>
        Usa per {slot === "giulia" ? "Giulia" : "Marco"}
      </button>
    )}
  </li>
);

export const AdminVoicePicker = ({ headers, info, onChange }) => {
  const [slot, setSlot] = useState("giulia");
  const [voices, setVoices] = useState({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(null);
  const current = SLOTS.find((s) => s.slot === slot);

  const load = async (s = current) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/elevenlabs/voci-italiane", { headers, params: { genere: s.genere } });
      setVoices((v) => ({ ...v, [s.slot]: Array.isArray(data) ? data : [] }));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  const play = (v) => {
    if (player) player.pause();
    if (playing === v.voice_id) return setPlaying(null);
    if (!v.preview_url) return toast.error("Anteprima non disponibile");
    player = new Audio(v.preview_url);
    player.onended = () => setPlaying(null);
    player.play().catch(() => setPlaying(null));
    setPlaying(v.voice_id);
  };

  const use = async (v, s) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/elevenlabs/usa-voce", { public_owner_id: v.public_owner_id, voice_id: v.voice_id, nome: v.name, slot: s }, { headers });
      onChange(data);
      toast.success(`${v.name} ora è la voce di ${s === "giulia" ? "Giulia" : "Marco"}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const list = voices[slot];
  return (
    <div className="mt-8 border-t border-[#f0f0f2] pt-6" data-testid="voice-picker">
      <p className="font-display text-base font-bold">Scegli le voci italiane</p>
      <p className="mt-1 text-sm text-[#52525b]">
        Giulia: <b>{info?.voce_giulia_nome || (info?.voce_giulia_id ? info.voce_giulia_id : "predefinita")}</b> · Marco: <b>{info?.voce_marco_nome || (info?.voce_marco_id ? info.voce_marco_id : "predefinita")}</b>
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        {SLOTS.map((s) => (
          <button key={s.slot} type="button" onClick={() => { setSlot(s.slot); if (!voices[s.slot]) load(s); }}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${slot === s.slot ? "bg-[#111111] text-white" : "bg-[#F5F5F7] text-[#111111] hover:bg-[#e7e7ea]"}`} data-testid={`voice-slot-${s.slot}`}>
            {s.label}
          </button>
        ))}
        {!list && (
          <button type="button" onClick={() => load()} disabled={loading} className="btn-blu h-9 px-4 text-sm" data-testid="load-italian-voices">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mostra voci italiane"}
          </button>
        )}
      </div>
      {loading && list && <Loader2 className="mt-4 h-5 w-5 animate-spin text-[#0066FF]" />}
      {list && (
        <ul className="mt-4 grid max-h-[26rem] gap-2 overflow-y-auto pr-1 md:grid-cols-2" data-lenis-prevent data-testid="italian-voices-list">
          {list.length === 0 && <li className="text-sm text-[#52525b]">Nessuna voce trovata.</li>}
          {list.map((v) => (
            <VoiceRow key={v.voice_id} v={v} slot={slot} busy={busy} onUse={use} playing={playing === v.voice_id} onPlay={play}
              chosen={info?.[`voce_${slot}_nome`] === v.name} />
          ))}
        </ul>
      )}
    </div>
  );
};
